/**
 * Image Generator State Management Hook
 *
 * Consolidates all image generator state into a single useReducer,
 * with preset CRUD, enhancement history, and localStorage persistence.
 */

import { useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { ModelDefinitionListItem, ImageGeneratorPreferences, api } from '@/lib/api';
import {
  ImageInstanceConfig,
  ImageInstanceState,
  ImagePreset,
  GeneratedImage,
  EnhancementKey,
  TagDefinition,
  ReferenceImage,
  NUM_INSTANCES,
  MAX_HISTORY_ITEMS,
  ENHANCEMENT_CATEGORIES,
  EMPTY_ENHANCEMENTS,
  getEnhancementHistoryStorageKey,
  getPresetsStorageKey,
  getInstanceConfigsStorageKey,
  getTagDefinitionsStorageKey,
  getInstanceImagesStorageKey,
  getPanelLayoutStorageKey,
  ARCHE_ID,
  createDefaultInstanceConfig,
  createDefaultInstanceState,
  isUnsavedPresetName,
  toUnsavedPresetName,
  isSizeSupportedByModel,
  pickDefaultSizeForModel,
} from '../types/image-generator.types';
import {
  ImageMode,
  BUILT_IN_MODES,
  resolveBuiltInModes,
  getModesStorageKey,
} from '../types/image-generator-modes';

// ============================================================================
// State Shape
// ============================================================================

export interface ImageGeneratorState {
  instanceConfigs: ImageInstanceConfig[];
  instanceStates: ImageInstanceState[];
  enabledInstances: boolean[];
  minimizedInstances: boolean[];
  instanceImages: GeneratedImage[][];
  enhancementHistory: Record<EnhancementKey, string[]>;
  presets: ImagePreset[];
  tagDefinitions: TagDefinition[];
  modes: ImageMode[];
}

// ============================================================================
// Action Types
// ============================================================================

type ImageGeneratorAction =
  | { type: 'UPDATE_CONFIG'; instanceId: number; updates: Partial<ImageInstanceConfig> }
  | { type: 'SET_CONFIGS'; configs: ImageInstanceConfig[] }
  | { type: 'UPDATE_STATE'; instanceId: number; updates: Partial<ImageInstanceState> }
  | { type: 'TOGGLE_INSTANCE'; instanceId: number }
  | { type: 'TOGGLE_MINIMIZED'; instanceId: number }
  | { type: 'ADD_IMAGE'; instanceId: number; image: GeneratedImage }
  | { type: 'REMOVE_IMAGE'; instanceId: number; requestId: string }
  | { type: 'CLEAR_ALL_IMAGES' }
  | { type: 'SET_IMAGES'; instanceId: number; images: GeneratedImage[] }
  | { type: 'ADD_PRESET'; preset: ImagePreset }
  | { type: 'DELETE_PRESET'; presetId: string }
  | { type: 'SET_ENHANCEMENT_HISTORY'; key: EnhancementKey; history: string[] }
  | { type: 'ADD_ENHANCEMENT_HISTORY'; key: EnhancementKey; value: string }
  | { type: 'UPDATE_ENHANCEMENT'; instanceId: number; key: EnhancementKey; value: string }
  | { type: 'CLEAR_ENHANCEMENT'; instanceId: number; key: EnhancementKey }
  | {
      type: 'UPDATE_PRESET';
      presetId: string;
      updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>;
    }
  | { type: 'OVERWRITE_PRESET'; presetId: string; instanceId: number }
  | { type: 'ADD_TAG_DEFINITION'; tag: TagDefinition }
  | { type: 'DELETE_TAG_DEFINITION'; tagId: string }
  | { type: 'RENAME_TAG_DEFINITION'; tagId: string; name: string }
  | { type: 'SET_REFERENCE_IMAGE'; instanceId: number; referenceImage: ReferenceImage }
  | { type: 'CLEAR_REFERENCE_IMAGE'; instanceId: number }
  | { type: 'ADD_MODE'; mode: ImageMode }
  | { type: 'DELETE_MODE'; modeId: string }
  | {
      type: 'UPDATE_MODE';
      modeId: string;
      updates: Partial<Omit<ImageMode, 'id' | 'createdAt' | 'builtIn'>>;
    }
  | { type: 'SET_BUILT_IN_MODES'; builtInModes: ImageMode[] };

// ============================================================================
// Reducer
// ============================================================================

function markConfigUnsaved(
  config: ImageInstanceConfig,
  presets: ImagePreset[],
): ImageInstanceConfig {
  if (!isUnsavedPresetName(config.presetName)) {
    const currentPreset = presets.find((p) => p.name === config.presetName);
    return {
      ...config,
      presetName: toUnsavedPresetName(config.presetName),
      originalPresetId: currentPreset?.id ?? config.originalPresetId ?? null,
    };
  }
  return config;
}

export function imageGeneratorReducer(
  state: ImageGeneratorState,
  action: ImageGeneratorAction,
): ImageGeneratorState {
  switch (action.type) {
    case 'UPDATE_CONFIG': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;

        const settingsKeys: (keyof ImageInstanceConfig)[] = [
          'includeModelInPreset',
          'modelName',
          'provider',
          'size',
          'quality',
          'style',
          'enhancements',
          'providerParams',
        ];
        const isSettingsChange = settingsKeys.some((key) => key in action.updates);
        const shouldMarkUnsaved =
          isSettingsChange &&
          !('presetName' in action.updates) &&
          !isUnsavedPresetName(config.presetName);

        if (shouldMarkUnsaved) {
          const currentPreset = state.presets.find((p) => p.name === config.presetName);
          return {
            ...config,
            ...action.updates,
            presetName: toUnsavedPresetName(config.presetName),
            originalPresetId: currentPreset?.id ?? config.originalPresetId ?? null,
          };
        }

        return {
          ...config,
          ...action.updates,
        };
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'SET_CONFIGS':
      return { ...state, instanceConfigs: action.configs };

    case 'UPDATE_STATE': {
      const newStates = state.instanceStates.map((s, i) =>
        i === action.instanceId ? { ...s, ...action.updates } : s,
      );
      return { ...state, instanceStates: newStates };
    }

    case 'TOGGLE_INSTANCE': {
      const newEnabled = [...state.enabledInstances];
      const enabledCount = newEnabled.filter(Boolean).length;
      if (newEnabled[action.instanceId] && enabledCount <= 1) {
        return state;
      }
      newEnabled[action.instanceId] = !newEnabled[action.instanceId];
      const newMinimized = [...state.minimizedInstances];
      // Un-minimize when enabling, reset minimized when disabling
      newMinimized[action.instanceId] = false;
      return { ...state, enabledInstances: newEnabled, minimizedInstances: newMinimized };
    }

    case 'TOGGLE_MINIMIZED': {
      const newMinimized = [...state.minimizedInstances];
      if (state.minimizedInstances[action.instanceId]) {
        newMinimized[action.instanceId] = false;
        return { ...state, minimizedInstances: newMinimized };
      }
      const enabledAndExpanded = state.enabledInstances.filter(
        (enabled, i) => enabled && !state.minimizedInstances[i],
      );
      if (enabledAndExpanded.length <= 1) {
        return state;
      }
      newMinimized[action.instanceId] = true;
      return { ...state, minimizedInstances: newMinimized };
    }

    case 'ADD_IMAGE': {
      const updated = [...state.instanceImages];
      if (updated[action.instanceId].some((img) => img.requestId === action.image.requestId)) {
        return state;
      }
      updated[action.instanceId] = [...updated[action.instanceId], action.image];
      return { ...state, instanceImages: updated };
    }

    case 'REMOVE_IMAGE': {
      const updated = [...state.instanceImages];
      updated[action.instanceId] = updated[action.instanceId].filter(
        (img) => img.requestId !== action.requestId,
      );
      return { ...state, instanceImages: updated };
    }

    case 'CLEAR_ALL_IMAGES': {
      return {
        ...state,
        instanceImages: state.instanceImages.map(() => []),
        instanceConfigs: state.instanceConfigs.map((config) => ({
          ...config,
          referenceImage: null,
        })),
      };
    }

    case 'SET_IMAGES': {
      const updated = [...state.instanceImages];
      updated[action.instanceId] = action.images;
      return { ...state, instanceImages: updated };
    }

    case 'ADD_PRESET':
      return { ...state, presets: [...state.presets, action.preset] };

    case 'DELETE_PRESET':
      return { ...state, presets: state.presets.filter((p) => p.id !== action.presetId) };

    case 'SET_ENHANCEMENT_HISTORY':
      return {
        ...state,
        enhancementHistory: { ...state.enhancementHistory, [action.key]: action.history },
      };

    case 'ADD_ENHANCEMENT_HISTORY': {
      const existing = state.enhancementHistory[action.key] || [];
      const filtered = existing.filter((item) => item !== action.value);
      return {
        ...state,
        enhancementHistory: {
          ...state.enhancementHistory,
          [action.key]: [action.value, ...filtered].slice(0, MAX_HISTORY_ITEMS),
        },
      };
    }

    case 'UPDATE_ENHANCEMENT': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;
        const updated = {
          ...config,
          enhancements: { ...config.enhancements, [action.key]: action.value },
        };
        return markConfigUnsaved(updated, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'CLEAR_ENHANCEMENT': {
      const newConfigs = state.instanceConfigs.map((config, i) => {
        if (i !== action.instanceId) return config;
        const updated = {
          ...config,
          enhancements: { ...config.enhancements, [action.key]: '' },
        };
        return markConfigUnsaved(updated, state.presets);
      });
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'UPDATE_PRESET': {
      const newPresets = state.presets.map((p) =>
        p.id === action.presetId ? { ...p, ...action.updates } : p,
      );
      return { ...state, presets: newPresets };
    }

    case 'OVERWRITE_PRESET': {
      const config = state.instanceConfigs[action.instanceId];
      if (!config) return state;
      const preset = state.presets.find((p) => p.id === action.presetId);
      if (!preset) return state;

      const presetUpdate: Partial<ImagePreset> = {
        size: config.size,
        quality: config.quality,
        style: config.style,
        enhancements: { ...config.enhancements },
        providerParams: { ...config.providerParams },
      };
      if (config.includeModelInPreset) {
        presetUpdate.modelName = config.modelName;
        presetUpdate.provider = config.provider;
      } else {
        presetUpdate.modelName = undefined;
        presetUpdate.provider = undefined;
      }

      const newPresets = state.presets.map((p) =>
        p.id === action.presetId ? { ...p, ...presetUpdate } : p,
      );
      const newConfigs = state.instanceConfigs.map((c, i) =>
        i === action.instanceId ? { ...c, presetName: preset.name, originalPresetId: null } : c,
      );
      return { ...state, presets: newPresets, instanceConfigs: newConfigs };
    }

    case 'ADD_TAG_DEFINITION':
      return { ...state, tagDefinitions: [...state.tagDefinitions, action.tag] };

    case 'DELETE_TAG_DEFINITION': {
      const newTagDefs = state.tagDefinitions.filter((t) => t.id !== action.tagId);
      const newPresets = state.presets.map((p) => ({
        ...p,
        tags: p.tags.filter((tagId) => tagId !== action.tagId),
      }));
      return { ...state, tagDefinitions: newTagDefs, presets: newPresets };
    }

    case 'RENAME_TAG_DEFINITION': {
      const newTagDefs = state.tagDefinitions.map((t) =>
        t.id === action.tagId ? { ...t, name: action.name } : t,
      );
      return { ...state, tagDefinitions: newTagDefs };
    }

    case 'SET_REFERENCE_IMAGE': {
      const newConfigs = state.instanceConfigs.map((config, i) =>
        i === action.instanceId ? { ...config, referenceImage: action.referenceImage } : config,
      );
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'CLEAR_REFERENCE_IMAGE': {
      const newConfigs = state.instanceConfigs.map((config, i) =>
        i === action.instanceId ? { ...config, referenceImage: null } : config,
      );
      return { ...state, instanceConfigs: newConfigs };
    }

    case 'ADD_MODE':
      return { ...state, modes: [...state.modes, action.mode] };

    case 'DELETE_MODE': {
      const modeToDelete = state.modes.find((m) => m.id === action.modeId);
      if (modeToDelete?.builtIn) return state;
      return { ...state, modes: state.modes.filter((m) => m.id !== action.modeId) };
    }

    case 'UPDATE_MODE': {
      const newModes = state.modes.map((m) => {
        if (m.id !== action.modeId) return m;
        // Built-in modes only allow tag updates
        if (m.builtIn) {
          if ('tags' in action.updates) {
            return { ...m, tags: action.updates.tags! };
          }
          return m;
        }
        return { ...m, ...action.updates };
      });
      return { ...state, modes: newModes };
    }

    case 'SET_BUILT_IN_MODES': {
      // Replace built-in modes while preserving user tag overrides and custom modes
      const userModes = state.modes.filter((m) => !m.builtIn);
      const existingTagOverrides: Record<string, string[]> = {};
      for (const m of state.modes) {
        if (m.builtIn && m.tags.length > 0) existingTagOverrides[m.id] = m.tags;
      }
      const resolvedBuiltIns = action.builtInModes.map((m) =>
        existingTagOverrides[m.id] ? { ...m, tags: existingTagOverrides[m.id] } : m,
      );
      return { ...state, modes: [...resolvedBuiltIns, ...userModes] };
    }

    default:
      return state;
  }
}

// ============================================================================
// Initial State Loader
// ============================================================================

function isValidInstanceConfig(c: unknown): c is ImageInstanceConfig {
  return (
    typeof c === 'object' &&
    c !== null &&
    typeof (c as ImageInstanceConfig).instanceId === 'number' &&
    typeof (c as ImageInstanceConfig).provider === 'string' &&
    typeof (c as ImageInstanceConfig).modelName === 'string'
  );
}

function isValidPreset(p: unknown): p is ImagePreset {
  return (
    typeof p === 'object' &&
    p !== null &&
    typeof (p as ImagePreset).id === 'string' &&
    typeof (p as ImagePreset).name === 'string' &&
    typeof (p as ImagePreset).size === 'string'
  );
}

function isValidTagDefinition(t: unknown): t is TagDefinition {
  return (
    typeof t === 'object' &&
    t !== null &&
    typeof (t as TagDefinition).id === 'string' &&
    typeof (t as TagDefinition).name === 'string'
  );
}

function loadInitialState(userId: string): ImageGeneratorState {
  let instanceConfigs: ImageInstanceConfig[];
  try {
    const saved = localStorage.getItem(getInstanceConfigsStorageKey(userId));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed) || !parsed.every(isValidInstanceConfig)) {
        throw new Error('Invalid instanceConfigs shape');
      }
      const savedConfigs: ImageInstanceConfig[] = parsed;
      instanceConfigs = Array.from({ length: NUM_INSTANCES }, (_, i) => {
        const savedConfig = savedConfigs.find((c) => c.instanceId === i);
        if (savedConfig) {
          return {
            ...createDefaultInstanceConfig(i),
            ...savedConfig,
            instanceId: i,
          };
        }
        return createDefaultInstanceConfig(i);
      });
    } else {
      instanceConfigs = Array.from({ length: NUM_INSTANCES }, (_, i) =>
        createDefaultInstanceConfig(i),
      );
    }
  } catch {
    instanceConfigs = Array.from({ length: NUM_INSTANCES }, (_, i) =>
      createDefaultInstanceConfig(i),
    );
  }

  // Migrate legacy configs: rename old preset, ensure providerParams and includeModelInPreset exist
  instanceConfigs = instanceConfigs.map((config) => ({
    ...config,
    presetName:
      config.presetName === 'Custom Preset (Unsaved)' ? 'Default Preset' : config.presetName,
    providerParams: config.providerParams || {},
    includeModelInPreset: config.includeModelInPreset ?? false,
  }));

  const emptyHistory = ENHANCEMENT_CATEGORIES.reduce(
    (acc, cat) => ({ ...acc, [cat.key]: [] }),
    {} as Record<EnhancementKey, string[]>,
  );
  let enhancementHistory: Record<EnhancementKey, string[]>;
  try {
    const saved = localStorage.getItem(getEnhancementHistoryStorageKey(userId));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Invalid enhancementHistory shape');
      }
      // Merge over defaults so new categories get empty arrays
      enhancementHistory = ENHANCEMENT_CATEGORIES.reduce(
        (acc, cat) => ({
          ...acc,
          [cat.key]: Array.isArray(parsed[cat.key]) ? parsed[cat.key] : [],
        }),
        {} as Record<EnhancementKey, string[]>,
      );
    } else {
      enhancementHistory = emptyHistory;
    }
  } catch {
    enhancementHistory = emptyHistory;
  }

  let presets: ImagePreset[];
  try {
    const saved = localStorage.getItem(getPresetsStorageKey(userId));
    const parsed = saved ? JSON.parse(saved) : [];
    if (!Array.isArray(parsed) || !parsed.every(isValidPreset)) {
      throw new Error('Invalid presets shape');
    }
    // Migrate existing presets that lack tags/categoryId/providerParams
    presets = parsed.map((p: ImagePreset) => ({
      ...p,
      tags: Array.isArray(p.tags) ? p.tags : [],
      categoryId: p.categoryId !== undefined ? p.categoryId : null,
      providerParams: p.providerParams || {},
    }));
  } catch {
    presets = [];
  }

  let tagDefinitions: TagDefinition[];
  try {
    const saved = localStorage.getItem(getTagDefinitionsStorageKey(userId));
    const parsed = saved ? JSON.parse(saved) : [];
    if (!Array.isArray(parsed) || !parsed.every(isValidTagDefinition)) {
      throw new Error('Invalid tagDefinitions shape');
    }
    tagDefinitions = parsed;
  } catch {
    tagDefinitions = [];
  }

  // Load user-created modes and merge with built-ins
  let userModes: ImageMode[] = [];
  try {
    const saved = localStorage.getItem(getModesStorageKey(userId));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        userModes = parsed.filter(
          (m: unknown) =>
            typeof m === 'object' &&
            m !== null &&
            typeof (m as ImageMode).id === 'string' &&
            typeof (m as ImageMode).name === 'string' &&
            Array.isArray((m as ImageMode).panelConfigs) &&
            (m as ImageMode).panelConfigs.length >= 3,
        );
      }
    }
  } catch {
    userModes = [];
  }
  // Load tag overrides for built-in modes
  let builtInTagOverrides: Record<string, string[]> = {};
  try {
    const savedOverrides = localStorage.getItem(`${getModesStorageKey(userId)}-builtin-tags`);
    if (savedOverrides) {
      builtInTagOverrides = JSON.parse(savedOverrides);
    }
  } catch {
    builtInTagOverrides = {};
  }
  const builtInModes = BUILT_IN_MODES.map((m) =>
    builtInTagOverrides[m.id] ? { ...m, tags: builtInTagOverrides[m.id] } : m,
  );
  const modes = [...builtInModes, ...userModes];

  // Load saved session images (URLs will be refreshed async after mount)
  let instanceImages: GeneratedImage[][] = Array.from(
    { length: NUM_INSTANCES },
    () => [] as GeneratedImage[],
  );
  try {
    const saved = localStorage.getItem(getInstanceImagesStorageKey(userId));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length === NUM_INSTANCES) {
        instanceImages = parsed.map((images: unknown) =>
          Array.isArray(images)
            ? images.filter(
                (img: unknown) =>
                  typeof img === 'object' &&
                  img !== null &&
                  typeof (img as GeneratedImage).requestId === 'string' &&
                  typeof (img as GeneratedImage).imageRef === 'string',
              )
            : [],
        );
      }
    }
  } catch {
    // Ignore corrupt data
  }

  // Load panel layout (enabled/minimized state)
  let enabledInstances: boolean[] = Array.from({ length: NUM_INSTANCES }, (_, i) => i < 3);
  let minimizedInstances: boolean[] = Array.from({ length: NUM_INSTANCES }, () => false);
  try {
    const saved = localStorage.getItem(getPanelLayoutStorageKey(userId));
    if (saved) {
      const parsed = JSON.parse(saved);
      if (
        parsed &&
        Array.isArray(parsed.enabledInstances) &&
        parsed.enabledInstances.length === NUM_INSTANCES &&
        Array.isArray(parsed.minimizedInstances) &&
        parsed.minimizedInstances.length === NUM_INSTANCES
      ) {
        enabledInstances = parsed.enabledInstances;
        minimizedInstances = parsed.minimizedInstances;
      }
    }
  } catch {
    // Ignore corrupt data
  }

  return {
    instanceConfigs,
    instanceStates: Array.from({ length: NUM_INSTANCES }, () => createDefaultInstanceState()),
    enabledInstances,
    minimizedInstances,
    instanceImages,
    enhancementHistory,
    presets,
    tagDefinitions,
    modes,
  };
}

// ============================================================================
// Hook Result Type
// ============================================================================

export interface UseImageGeneratorStateOptions {
  /** The authenticated user's ID, used to scope localStorage keys */
  userId: string;
  /** Callback invoked when preferences change (for session persistence) */
  onPreferencesChange?: (prefs: Partial<ImageGeneratorPreferences>) => void;
}

export interface UseImageGeneratorStateResult {
  state: ImageGeneratorState;
  // Config helpers
  updateInstanceConfig: (instanceId: number, updates: Partial<ImageInstanceConfig>) => void;
  updateInstanceState: (instanceId: number, updates: Partial<ImageInstanceState>) => void;
  toggleInstance: (instanceId: number) => void;
  toggleMinimized: (instanceId: number) => void;
  canMinimize: (instanceId: number) => boolean;
  // Image helpers
  addImage: (instanceId: number, image: GeneratedImage) => void;
  removeImage: (instanceId: number, requestId: string) => void;
  clearAllImages: () => void;
  // Preset helpers
  addPreset: (preset: ImagePreset) => void;
  savePreset: (name: string, instanceId: number) => void;
  applyPresetToInstance: (instanceId: number, preset: ImagePreset | null) => void;
  overwritePreset: (presetId: string, instanceId: number) => void;
  deletePreset: (presetId: string) => void;
  updatePreset: (presetId: string, updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>) => void;
  // Tag helpers
  addTagDefinition: (name: string) => string;
  deleteTagDefinition: (tagId: string) => void;
  renameTagDefinition: (tagId: string, name: string) => void;
  // Mode helpers
  applyMode: (mode: ImageMode) => void;
  saveMode: (name: string) => void;
  deleteMode: (modeId: string) => void;
  updateMode: (
    modeId: string,
    updates: Partial<Omit<ImageMode, 'id' | 'createdAt' | 'builtIn'>>,
  ) => void;
  // Reference image helpers
  setReferenceImage: (instanceId: number, referenceImage: ReferenceImage) => void;
  clearReferenceImage: (instanceId: number) => void;
  // Enhancement helpers
  updateEnhancement: (instanceId: number, key: EnhancementKey, value: string) => void;
  clearEnhancement: (instanceId: number, key: EnhancementKey) => void;
  selectFromHistory: (instanceId: number, key: EnhancementKey, value: string) => void;
  // Sync helper
  syncConfigsWithModels: (models: ModelDefinitionListItem[]) => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useImageGeneratorState(
  options: UseImageGeneratorStateOptions,
): UseImageGeneratorStateResult {
  const { userId, onPreferencesChange } = options;
  const [state, dispatch] = useReducer(imageGeneratorReducer, userId, loadInitialState);

  // --- localStorage persistence effects ---

  useEffect(() => {
    localStorage.setItem(
      getEnhancementHistoryStorageKey(userId),
      JSON.stringify(state.enhancementHistory),
    );
  }, [userId, state.enhancementHistory]);

  useEffect(() => {
    localStorage.setItem(getPresetsStorageKey(userId), JSON.stringify(state.presets));
  }, [userId, state.presets]);

  useEffect(() => {
    localStorage.setItem(getTagDefinitionsStorageKey(userId), JSON.stringify(state.tagDefinitions));
  }, [userId, state.tagDefinitions]);

  useEffect(() => {
    const userModes = state.modes.filter((m) => !m.builtIn);
    localStorage.setItem(getModesStorageKey(userId), JSON.stringify(userModes));
    // Persist tag overrides for built-in modes
    const tagOverrides: Record<string, string[]> = {};
    for (const mode of state.modes) {
      if (mode.builtIn && mode.tags.length > 0) {
        tagOverrides[mode.id] = mode.tags;
      }
    }
    localStorage.setItem(
      `${getModesStorageKey(userId)}-builtin-tags`,
      JSON.stringify(tagOverrides),
    );
  }, [userId, state.modes]);

  useEffect(() => {
    localStorage.setItem(
      getInstanceConfigsStorageKey(userId),
      JSON.stringify(state.instanceConfigs),
    );
  }, [userId, state.instanceConfigs]);

  useEffect(() => {
    localStorage.setItem(
      getPanelLayoutStorageKey(userId),
      JSON.stringify({
        enabledInstances: state.enabledInstances,
        minimizedInstances: state.minimizedInstances,
      }),
    );
  }, [userId, state.enabledInstances, state.minimizedInstances]);

  // Persist session images to localStorage (strip imageUrl — it's a presigned URL that expires)
  useEffect(() => {
    const stripped = state.instanceImages.map((images) =>
      images.map(({ imageUrl: _, ...rest }) => rest),
    );
    localStorage.setItem(getInstanceImagesStorageKey(userId), JSON.stringify(stripped));
  }, [userId, state.instanceImages]);

  // Refresh presigned URLs for restored images on mount
  const hasRefreshedUrls = useRef(false);
  useEffect(() => {
    if (hasRefreshedUrls.current) return;
    hasRefreshedUrls.current = true;

    const imagesToRefresh = state.instanceImages.flatMap((images, instanceId) =>
      images.filter((img) => img.imageRef && !img.imageUrl).map((img) => ({ instanceId, img })),
    );

    if (imagesToRefresh.length === 0) return;

    Promise.all(
      imagesToRefresh.map(async ({ instanceId, img }) => {
        try {
          const url = await api.getImageUrl(ARCHE_ID, img.imageRef);
          return { instanceId, requestId: img.requestId, url };
        } catch {
          return { instanceId, requestId: img.requestId, url: '' };
        }
      }),
    ).then((results) => {
      // Group by instance and dispatch SET_IMAGES with refreshed URLs
      for (let i = 0; i < NUM_INSTANCES; i++) {
        const updates = results.filter((r) => r.instanceId === i);
        if (updates.length === 0) continue;

        const urlMap = new Map(updates.map((u) => [u.requestId, u.url]));
        const refreshed = state.instanceImages[i].map((img) => ({
          ...img,
          imageUrl: urlMap.get(img.requestId) ?? img.imageUrl,
        }));
        // Remove images that failed to refresh (deleted from storage)
        const valid = refreshed.filter((img) => img.imageUrl !== '');
        dispatch({ type: 'SET_IMAGES', instanceId: i, images: valid });
      }
    });
  }, []);

  // --- Session persistence: sync first instance config to server ---
  // Skip the initial render to avoid saving defaults on mount (matches SingleAI/MultiAI pattern)
  const firstConfig = state.instanceConfigs[0];
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (onPreferencesChange) {
      onPreferencesChange({
        selectedProvider: firstConfig.provider as ImageGeneratorPreferences['selectedProvider'],
        selectedModelId: firstConfig.modelName,
        imageSettings: {
          size: firstConfig.size,
          quality: firstConfig.quality,
          style: firstConfig.style,
        },
      });
    }
  }, [
    firstConfig.provider,
    firstConfig.modelName,
    firstConfig.size,
    firstConfig.quality,
    firstConfig.style,
    onPreferencesChange,
  ]);

  // --- Dispatch-wrapping callbacks ---

  const updateInstanceConfig = useCallback(
    (instanceId: number, updates: Partial<ImageInstanceConfig>) => {
      dispatch({ type: 'UPDATE_CONFIG', instanceId, updates });
    },
    [],
  );

  const updateInstanceState = useCallback(
    (instanceId: number, updates: Partial<ImageInstanceState>) => {
      dispatch({ type: 'UPDATE_STATE', instanceId, updates });
    },
    [],
  );

  const toggleInstance = useCallback((instanceId: number) => {
    dispatch({ type: 'TOGGLE_INSTANCE', instanceId });
  }, []);

  const toggleMinimized = useCallback((instanceId: number) => {
    dispatch({ type: 'TOGGLE_MINIMIZED', instanceId });
  }, []);

  const canMinimize = useCallback(
    (instanceId: number) => {
      if (state.minimizedInstances[instanceId]) return true;
      const enabledAndExpanded = state.enabledInstances.filter(
        (enabled, i) => enabled && !state.minimizedInstances[i],
      );
      return enabledAndExpanded.length > 1;
    },
    [state.enabledInstances, state.minimizedInstances],
  );

  const addImage = useCallback((instanceId: number, image: GeneratedImage) => {
    dispatch({ type: 'ADD_IMAGE', instanceId, image });
  }, []);

  const clearAllImages = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_IMAGES' });
  }, []);

  const removeImage = useCallback((instanceId: number, requestId: string) => {
    dispatch({ type: 'REMOVE_IMAGE', instanceId, requestId });
  }, []);

  const addPreset = useCallback((preset: ImagePreset) => {
    dispatch({ type: 'ADD_PRESET', preset });
  }, []);

  const savePreset = useCallback(
    (name: string, instanceId: number) => {
      const config = state.instanceConfigs[instanceId];
      const preset: ImagePreset = {
        id: `preset_${crypto.randomUUID()}`,
        name,
        size: config.size,
        quality: config.quality,
        style: config.style,
        enhancements: { ...config.enhancements },
        providerParams: { ...config.providerParams },
        ...(config.includeModelInPreset
          ? { modelName: config.modelName, provider: config.provider }
          : {}),
        createdAt: new Date().toISOString(),
        tags: [],
        categoryId: null,
      };
      dispatch({ type: 'ADD_PRESET', preset });
      dispatch({
        type: 'UPDATE_CONFIG',
        instanceId,
        updates: { presetName: name },
      });
    },
    [state.instanceConfigs],
  );

  const applyPresetToInstance = useCallback((instanceId: number, preset: ImagePreset | null) => {
    if (preset === null) {
      dispatch({
        type: 'UPDATE_CONFIG',
        instanceId,
        updates: {
          presetName: 'Default Preset',
          originalPresetId: null,
          includeModelInPreset: false,
          size: '1:1',
          quality: 'standard',
          style: 'vivid',
          enhancements: { ...EMPTY_ENHANCEMENTS },
          providerParams: {},
          referenceImage: null,
        },
      });
    } else {
      const updates: Partial<ImageInstanceConfig> = {
        presetName: preset.name,
        originalPresetId: preset.id,
        includeModelInPreset: !!preset.modelName,
        size: preset.size,
        quality: preset.quality,
        style: preset.style,
        enhancements: { ...preset.enhancements },
        providerParams: { ...(preset.providerParams || {}) },
        referenceImage: null,
      };
      if (preset.modelName) {
        updates.modelName = preset.modelName;
        updates.provider = preset.provider;
      }
      dispatch({ type: 'UPDATE_CONFIG', instanceId, updates });
    }
  }, []);

  const overwritePreset = useCallback((presetId: string, instanceId: number) => {
    dispatch({ type: 'OVERWRITE_PRESET', presetId, instanceId });
  }, []);

  const deletePreset = useCallback((presetId: string) => {
    dispatch({ type: 'DELETE_PRESET', presetId });
  }, []);

  const updatePreset = useCallback(
    (presetId: string, updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>) => {
      dispatch({ type: 'UPDATE_PRESET', presetId, updates });
    },
    [],
  );

  const addTagDefinition = useCallback((name: string): string => {
    const tag: TagDefinition = { id: `tag_${crypto.randomUUID()}`, name };
    dispatch({ type: 'ADD_TAG_DEFINITION', tag });
    return tag.id;
  }, []);

  const deleteTagDefinition = useCallback((tagId: string) => {
    dispatch({ type: 'DELETE_TAG_DEFINITION', tagId });
  }, []);

  const renameTagDefinition = useCallback((tagId: string, name: string) => {
    dispatch({ type: 'RENAME_TAG_DEFINITION', tagId, name });
  }, []);

  const applyMode = useCallback(
    (mode: ImageMode) => {
      mode.panelConfigs.forEach((panelConfig, i) => {
        dispatch({
          type: 'UPDATE_CONFIG',
          instanceId: i,
          updates: {
            presetName: mode.name,
            originalPresetId: null,
            modelName: panelConfig.modelName,
            provider: panelConfig.provider,
            size: panelConfig.size,
            quality: panelConfig.quality,
            style: panelConfig.style,
            enhancements: { ...panelConfig.enhancements },
            providerParams: { ...panelConfig.providerParams },
            referenceImage: null,
          },
        });
      });
      // Un-minimize all panels
      for (let i = 0; i < NUM_INSTANCES; i++) {
        if (state.minimizedInstances[i]) {
          dispatch({ type: 'TOGGLE_MINIMIZED', instanceId: i });
        }
      }
    },
    [state.minimizedInstances],
  );

  const saveMode = useCallback(
    (name: string) => {
      const panelConfigs = state.instanceConfigs.map((config) => ({
        modelName: config.modelName,
        provider: config.provider,
        size: config.size,
        quality: config.quality,
        style: config.style,
        enhancements: { ...config.enhancements },
        providerParams: { ...config.providerParams },
      }));

      const mode: ImageMode = {
        id: `mode_${crypto.randomUUID()}`,
        name,
        description: '',
        builtIn: false,
        panelConfigs,
        tags: [],
        createdAt: new Date().toISOString(),
      };
      dispatch({ type: 'ADD_MODE', mode });
    },
    [state.instanceConfigs],
  );

  const deleteMode = useCallback((modeId: string) => {
    dispatch({ type: 'DELETE_MODE', modeId });
  }, []);

  const updateMode = useCallback(
    (modeId: string, updates: Partial<Omit<ImageMode, 'id' | 'createdAt' | 'builtIn'>>) => {
      dispatch({ type: 'UPDATE_MODE', modeId, updates });
    },
    [],
  );

  const setReferenceImage = useCallback((instanceId: number, referenceImage: ReferenceImage) => {
    dispatch({ type: 'SET_REFERENCE_IMAGE', instanceId, referenceImage });
  }, []);

  const clearReferenceImage = useCallback((instanceId: number) => {
    dispatch({ type: 'CLEAR_REFERENCE_IMAGE', instanceId });
  }, []);

  const updateEnhancement = useCallback(
    (instanceId: number, key: EnhancementKey, value: string) => {
      dispatch({ type: 'UPDATE_ENHANCEMENT', instanceId, key, value });
    },
    [],
  );

  const clearEnhancement = useCallback(
    (instanceId: number, key: EnhancementKey) => {
      const currentValue = state.instanceConfigs[instanceId]?.enhancements[key]?.trim();
      if (currentValue) {
        dispatch({ type: 'ADD_ENHANCEMENT_HISTORY', key, value: currentValue });
      }
      dispatch({ type: 'CLEAR_ENHANCEMENT', instanceId, key });
    },
    [state.instanceConfigs],
  );

  const selectFromHistory = useCallback(
    (instanceId: number, key: EnhancementKey, value: string) => {
      dispatch({ type: 'UPDATE_ENHANCEMENT', instanceId, key, value });
    },
    [],
  );

  const syncConfigsWithModels = useCallback(
    (models: ModelDefinitionListItem[]) => {
      if (models.length === 0) return;
      // Resolve dynamic built-in modes (e.g. Provider Shootout) from entities
      dispatch({ type: 'SET_BUILT_IN_MODES', builtInModes: resolveBuiltInModes(models) });

      const newConfigs = state.instanceConfigs.map((config) => {
        const isValidSelection = models.some((m) => m.modelName === config.modelName);
        let next = config;
        if (!isValidSelection) {
          next = {
            ...next,
            modelName: models[0].modelName,
            provider: models[0].provider,
          };
        }
        // After (potentially) swapping the model, ensure the persisted size is
        // still compatible with the resulting model's supportedSizes — guards
        // against stale localStorage configs and entity-capability changes.
        if (!isSizeSupportedByModel(next.size, next.modelName, models)) {
          const fallback = pickDefaultSizeForModel(next.modelName, models);
          if (fallback) next = { ...next, size: fallback };
        }
        return next;
      });
      const hasChanges = newConfigs.some(
        (c, i) =>
          c.modelName !== state.instanceConfigs[i].modelName ||
          c.provider !== state.instanceConfigs[i].provider ||
          c.size !== state.instanceConfigs[i].size,
      );
      if (hasChanges) {
        dispatch({ type: 'SET_CONFIGS', configs: newConfigs });
      }
    },
    [state.instanceConfigs],
  );

  return useMemo(
    () => ({
      state,
      updateInstanceConfig,
      updateInstanceState,
      toggleInstance,
      toggleMinimized,
      canMinimize,
      addImage,
      removeImage,
      clearAllImages,
      addPreset,
      savePreset,
      applyPresetToInstance,
      overwritePreset,
      deletePreset,
      updatePreset,
      addTagDefinition,
      deleteTagDefinition,
      renameTagDefinition,
      setReferenceImage,
      clearReferenceImage,
      applyMode,
      saveMode,
      deleteMode,
      updateMode,
      updateEnhancement,
      clearEnhancement,
      selectFromHistory,
      syncConfigsWithModels,
    }),
    [
      state,
      updateInstanceConfig,
      updateInstanceState,
      toggleInstance,
      toggleMinimized,
      canMinimize,
      addImage,
      removeImage,
      clearAllImages,
      addPreset,
      savePreset,
      applyPresetToInstance,
      overwritePreset,
      deletePreset,
      updatePreset,
      addTagDefinition,
      deleteTagDefinition,
      renameTagDefinition,
      setReferenceImage,
      clearReferenceImage,
      applyMode,
      saveMode,
      deleteMode,
      updateMode,
      updateEnhancement,
      clearEnhancement,
      selectFromHistory,
      syncConfigsWithModels,
    ],
  );
}
